import { z } from 'zod';
import { sha256 } from '@noble/hashes/sha256';

// src/models/generated/commons_interfaces_canonical_semantic.ts
var SemanticActorSchema = z.object({ "role": z.any().default("other"), "address": z.string().describe("Lowercase 0x-prefixed address."), "chain_id": z.union([z.string(), z.null()]).describe("Chain the address lives on. Differs from the tx chain for the destination side of a bridge node.").default(null), "entity_id": z.union([z.string(), z.null()]).describe("Resolved catalog entity id (populated post-decode).").default(null), "label": z.union([z.string(), z.null()]).describe("Human label/name for the address, if known.").default(null) }).describe("An address participating in a semantic node, with optional\ncatalog/entity decoration filled in by the pipeline annotate step.");
var SemanticAssetSchema = z.object({ "token_address": z.union([z.string(), z.null()]).describe("Token contract; None / 'native' for the chain's gas token.").default(null), "symbol": z.union([z.string(), z.null()]).describe("e.g. 'LINK', 'USDC'.").default(null), "decimals": z.union([z.number().int().gte(0).lte(36), z.null()]).default(null), "amount_raw": z.union([z.string(), z.null()]).describe("Unscaled integer amount as a decimal string (no precision loss).").default(null), "token_id": z.union([z.string(), z.null()]).describe("The id of a non-fungible (ERC-721) or multi-token (ERC-1155) asset, as a decimal string. ``amount_raw`` is then the count of that id (1 for an ERC-721) \u2014 the same convention as ``TokenFlow.token_id``.").default(null), "amount": z.union([z.string(), z.null()]).describe("Human-formatted amount ('2747.8') when decimals are known.").default(null), "usd": z.union([z.string(), z.null()]).describe("USD value as a string, when a price was available.").default(null), "direction": z.union([z.string(), z.null()]).describe("'in' | 'out' relative to the node's initiator.").default(null), "basis": z.union([z.string(), z.null()]).describe("What ``amount`` represents (an :class:`AmountBasis` value): 'actual' (the settled amount, the default) or 'quoted_minimum' (a swap min-return floor read from the call). None == unspecified.").default(null), "min_amount_raw": z.union([z.string(), z.null()]).describe("The quoted-minimum (floor) as an unscaled integer string, present only when ``amount`` is the reconciled ACTUAL and a floor was also known \u2014 so the UI can render 'actual (min: floor)'.").default(null), "min_amount": z.union([z.string(), z.null()]).describe("Human-formatted floor matching ``min_amount_raw``.").default(null) }).describe("A value movement expressed in human units where possible.");
var SemanticSourceRefSchema = z.object({ "ref_type": z.string().describe("'top_call' | 'log' | 'internal_tx' | 'nested_calldata'."), "address": z.union([z.string(), z.null()]).default(null), "log_index": z.union([z.number().int(), z.null()]).default(null), "internal_tx_index": z.union([z.number().int(), z.null()]).default(null), "path": z.array(z.string()).describe("Param path walked into nested calldata (e.g. ['message', 'data'] or ['commands', '2']).").optional() }).describe("A back-link from a semantic node to the raw decode artifact it was\nderived from, so a UI can drill down to the EVM-level detail.");
z.object({ "kind": z.any().default("abi"), "id": z.union([z.string(), z.null()]).default(null), "external_id": z.union([z.string(), z.null()]).default(null), "url": z.union([z.string(), z.null()]).default(null), "raw_payload_ref": z.union([z.string(), z.null()]).default(null), "visibility": z.union([z.any(), z.null()]).default(null) }).describe('Who / how asserted an :class:`Evidence` entry.\n\n``id`` is the source-local identifier: a provider slug (``"moralis"``), an\nABI source (``"sourcify"``), a protocol-plugin id, or a user id \u2014 depending\non :attr:`kind`. For ``kind == PROVIDER`` the id is normalised to a\ncanonical provider slug via :func:`commons.providers.normalize_provider_id`\n(mirrors the old ``ProviderAttribution.provider_id`` discipline).');
var EvidenceSchema = z.object({ "source": z.any().optional(), "confidence": z.number().gte(0).lte(1).default(1), "reasons": z.array(z.string()).optional(), "signals": z.record(z.string(), z.any()).optional(), "asserted_fields": z.array(z.string()).optional(), "asserted_at": z.union([z.number().int(), z.null()]).default(null), "last_seen_at": z.union([z.number().int(), z.null()]).default(null), "context": z.record(z.string(), z.any()).optional(), "metadata": z.record(z.string(), z.any()).optional() }).describe("One assertion: *this source claims X, with this confidence, because \u2026*.\n\nReplaces both ``SemanticEvidence`` (decode) and ``ProviderAttribution``\n(catalog). ``confidence`` is clamped to ``[0, 1]``. ``asserted_at`` /\n``last_seen_at`` default to wall-clock seconds so a fresh assertion merges\nwith an older one without a ``None`` branch (see\n:func:`commons.evidence.engine.merge_evidence`).\n\nDecode-specific coordinates (chain / block / cross-chain hop / proxy\ntraversal) live in :attr:`context` under the ``CTX_*`` keys; read them via\nthe convenience properties (:attr:`chain_id`, :attr:`dest_chain_id`, \u2026).");
var TokenFlowSchema = z.object({ "kind": z.enum(["native", "internal", "closure", "erc20", "erc721", "erc1155", "spl", "hip1", "metaprotocol"]), "token_address": z.union([z.string(), z.null()]).default(null), "from_address": z.union([z.string(), z.null()]).default(null), "to_address": z.union([z.string(), z.null()]).default(null), "raw_amount": z.string().default("0"), "token_id": z.union([z.string(), z.null()]).default(null), "log_index": z.union([z.number().int(), z.null()]).default(null), "decimals": z.union([z.number().int(), z.null()]).default(null), "price_usd": z.union([z.number(), z.null()]).default(null), "value_usd": z.union([z.number(), z.null()]).default(null) }).describe('One value movement inside a transaction (native / internal / ERC-20 / NFT).\n\nCarries the raw on-chain amount plus the token contract so a later step can\nprice it; ``token_address is None`` means the chain\'s native asset. For a\nnon-fungible (``erc721``) or multi-token (``erc1155``) move, ``token_id``\ncarries the moved id and ``raw_amount`` is the count (``"1"`` for ERC-721).');
z.object({ "address": z.string(), "token_address": z.union([z.string(), z.null()]).default(null), "raw_delta": z.string().default("0"), "value_usd": z.union([z.number(), z.null()]).default(null) }).describe('Net per-(address, token) delta across a transaction\'s flows.\n\nThe "net flow" a tx-table shows next to a complex DeFi tx: positive\n``raw_delta`` is a net inflow to ``address``, negative a net outflow.');
z.object({ "token_address": z.string(), "chain_id": z.string(), "symbol": z.union([z.string(), z.null()]).default(null), "decimals": z.union([z.number().int(), z.null()]).default(null), "price_usd": z.union([z.number(), z.null()]).default(null), "block_number": z.union([z.number().int(), z.null()]).default(null), "block_timestamp": z.union([z.number().int(), z.null()]).default(null), "source": z.union([z.string(), z.null()]).default(null), "confidence": z.union([z.number(), z.null()]).default(null), "metadata": z.record(z.string(), z.any()).optional() }).describe("A resolved token price, optionally as-of a specific block.\n\nProvider-agnostic: a price source (Moralis today, others later) maps its\npayload onto this shape. ``confidence`` lets a multi-source resolver pick or\nblend.");

// src/transpose/types.ts
var NATIVE_COIN_FLOW_KINDS = /* @__PURE__ */ new Set([
  "native",
  "internal",
  "closure"
]);
var TOKEN_FLOW_KINDS = new Set(
  TokenFlowSchema.shape.kind.options.filter((kind) => !NATIVE_COIN_FLOW_KINDS.has(kind))
);

// src/chains/family.ts
var ChainFamily = {
  EVM: "evm",
  UTXO: "utxo",
  SOLANA: "solana",
  COSMOS: "cosmos",
  /** Hyperliquid's L1 (HyperCore): one signed action per transaction, EVM-shaped identifiers. */
  HYPERCORE: "hypercore",
  OTHER: "other"
};
var EVM_IDENTIFIER_FAMILIES = /* @__PURE__ */ new Set([
  ChainFamily.EVM,
  ChainFamily.HYPERCORE
]);
function hasEvmIdentifiers(family) {
  return family != null && EVM_IDENTIFIER_FAMILIES.has(family);
}

// src/chains/seed-data.ts
var CHAIN_MAPPING = {
  // --- EVM mainnets ---
  "0x1": "eth",
  "0x3d": "etc",
  "0x89": "polygon",
  "0x38": "bsc",
  "0xcc": "opbnb",
  "0xa86a": "avalanche",
  "0xfa": "fantom",
  "0x19": "cronos",
  "0x2a15c308d": "palm",
  "0xa4b1": "arbitrum",
  "0x64": "gnosis",
  "0x15b38": "chiliz",
  "0x2105": "base",
  "0xa": "optimism",
  "0x2019": "klaytn",
  "0xa4ec": "celo",
  "0x8ae": "kava",
  "0x440": "metis",
  "0x63564c40": "harmony",
  "0x171": "pulsechain",
  "0x1388": "mantle",
  // Nesa. A Cosmos-SDK chain with an EVM module, so its Cosmos chain id
  // (`nesa_41443-1`) and its EVM chain id are the same 41443. Ported from
  // `_seed_data.py`, which has carried it since the Hyperlane domain fallback
  // was retired — without this row every spelling of Nesa falls through the
  // registry unresolved and renders as a raw `eip155:41443`.
  "0xa1e3": "nesa",
  "0x505": "moonriver",
  "0x504": "moonbeam",
  "0x144": "zkSync_era",
  "0x44d": "polygon_zkevm",
  "0xe708": "linea",
  "0x82750": "scroll",
  "0x13e31": "blast",
  "0x4e454152": "aurora",
  "0x7a": "fuse",
  "0x2eb": "flow",
  "0x7e4": "ronin",
  "0x46f": "lisk",
  "0x1b58": "zetachain",
  "0x531": "sei",
  // HyperEVM. Its L1 order book (HyperCore) is a non-EVM chain in registry.ts.
  "0x3e7": "hyperevm",
  // Ink, Kraken's OP Stack L2 — ported from `_seed_data.py` (CCIP + LayerZero route to it).
  "0xdef1": "ink",
  "0x8f": "monad",
  "0x2611": "plasma",
  // peaq. Ported from `_seed_data.py`, where it was seeded because a LayerZero
  // OFT leg named its endpoint id and the chip rendered the bare number. The
  // lower-case name is the project's own styling.
  "0xd0a": "peaq",
  // GRVT, a ZK Stack chain. This id was seeded as `zkSync_era_testnet`, so
  // `eip155:325` resolved as a zkSync Era testnet paired with Era mainnet; Era's
  // testnets are 280 and 300. Ported from `_seed_data.py`, which carries the
  // sources for the id and for the native token (GBT, not ETH).
  "0x145": "grvt",
  // --- EVM testnets ---
  "0x5": "goerli",
  "0xaa36a7": "sepolia",
  "0x4268": "holesky",
  "0x13881": "mumbai",
  "0x13882": "polygon_amoy",
  "0x61": "bsc_testnet",
  "0x27d8": "gnosis_chiado",
  "0x15b32": "chiliz_spicy",
  "0x14a33": "base_goerli",
  "0x14a34": "base_sepolia",
  "0x1a4": "optimism_goerli",
  "0xaa37dc": "optimism_sepolia",
  "0x66eee": "arbitrum_sepolia",
  "0x3e9": "klaytn_baobab",
  "0xaef3": "celo_alfajores",
  "0x8ad": "kava_testnet",
  "0x24c": "metis_goerli",
  "0x6357d2e0": "harmony_testnet",
  "0x4e454153": "aurora_testnet",
  "0x507": "moonbase",
  "0x118": "zkSync_testnet",
  "0x12c": "zksync_sepolia",
  "0x7b": "fuse_testnet",
  "0xe704": "linea_testnet",
  "0xe705": "linea_sepolia",
  "0x8274f": "scroll_testnet",
  "0x138b": "mantle_sepolia",
  "0xa0c71fd": "blast_sepolia",
  "0x98a": "polygon_zkevm_cardona",
  "0xfa2": "fantom_testnet",
  "0x106a": "lisk_sepolia",
  "0x221": "flow_testnet",
  "0x7e5": "ronin_saigon",
  "0x1b59": "zetachain_testnet",
  "0x530": "sei_testnet",
  "0x3e6": "hyperevm_testnet"
};
var TESTNET_CHAINS = [
  "goerli",
  "sepolia",
  "mumbai",
  "bsc_testnet",
  "gnosis_chiado",
  "chiliz_spicy",
  "base_goerli",
  "optimism_goerli",
  "klaytn_baobab",
  "celo_alfajores",
  "kava_testnet",
  "metis_goerli",
  "harmony_testnet",
  "zkSync_testnet",
  "aurora_testnet",
  "moonbase",
  "fuse_testnet",
  "linea_testnet",
  "scroll_testnet",
  "holesky",
  "polygon_amoy",
  "base_sepolia",
  "optimism_sepolia",
  "arbitrum_sepolia",
  "linea_sepolia",
  "blast_sepolia",
  "mantle_sepolia",
  "zksync_sepolia",
  "polygon_zkevm_cardona",
  "fantom_testnet",
  "lisk_sepolia",
  "flow_testnet",
  "ronin_saigon",
  "zetachain_testnet",
  "sei_testnet",
  "hyperevm_testnet"
];
var LAYER1_CHAINS = [
  "eth",
  "etc",
  "bsc",
  "avalanche",
  "fantom",
  "klaytn",
  "celo",
  "kava",
  "harmony",
  "moonriver",
  "moonbeam",
  "fuse",
  "pulsechain",
  "flow",
  "ronin",
  "sei",
  "monad",
  "peaq",
  "zetachain",
  "hyperevm"
];
var LAYER2_CHAINS = [
  "polygon",
  "arbitrum",
  "optimism",
  "metis",
  "base",
  "zkSync_era",
  "linea",
  "scroll",
  "mantle",
  "opbnb",
  "polygon_zkevm",
  "blast",
  "lisk",
  "grvt",
  "ink"
];
var SIDECHAINS = ["palm", "gnosis", "cronos", "aurora"];
var CHAIN_CONFIG = {
  "0x1": { token: "ETH", gasUnit: "Gwei", name: "Ethereum" },
  "0x3d": { token: "ETC", gasUnit: "Gwei", name: "Ethereum Classic" },
  "0x89": { token: "MATIC", gasUnit: "Gwei", name: "Polygon" },
  "0x38": { token: "BNB", gasUnit: "Gwei", name: "BNB Chain" },
  // Gas on Arbitrum is ETH; ARB is a governance token.
  "0xa4b1": { token: "ETH", gasUnit: "Gwei", name: "Arbitrum" },
  "0x2105": { token: "ETH", gasUnit: "Gwei", name: "Base" },
  "0xa": { token: "ETH", gasUnit: "Gwei", name: "Optimism" },
  "0xe708": { token: "ETH", gasUnit: "Gwei", name: "Linea" },
  "0xa86a": { token: "AVAX", gasUnit: "nAVAX", name: "Avalanche" },
  "0xfa": { token: "FTM", gasUnit: "Gwei", name: "Fantom" },
  "0x19": { token: "CRO", gasUnit: "Gwei", name: "Cronos" },
  "0x64": { token: "xDAI", gasUnit: "Gwei", name: "Gnosis" },
  "0x15b38": { token: "CHZ", gasUnit: "Gwei", name: "Chiliz" },
  "0x171": { token: "PLS", gasUnit: "Gwei", name: "PulseChain" },
  "0x504": { token: "GLMR", gasUnit: "Gwei", name: "Moonbeam" },
  "0x505": { token: "MOVR", gasUnit: "Gwei", name: "Moonriver" },
  "0x13e31": { token: "ETH", gasUnit: "Gwei", name: "Blast" },
  "0x144": { token: "ETH", gasUnit: "Gwei", name: "zkSync Era" },
  "0x1388": { token: "MNT", gasUnit: "Gwei", name: "Mantle" },
  "0xa1e3": { token: "NES", gasUnit: "Gwei", name: "Nesa" },
  "0xd0a": { token: "PEAQ", gasUnit: "Gwei", name: "peaq" },
  // GRVT pays gas in GBT (its ZKsync Bridgehub base token), not ETH.
  "0x145": { token: "GBT", gasUnit: "Gwei", name: "GRVT" },
  "0xcc": { token: "BNB", gasUnit: "Gwei", name: "opBNB" },
  // Gas on Polygon zkEVM is ETH, not MATIC.
  "0x44d": { token: "ETH", gasUnit: "Gwei", name: "Polygon zkEVM" },
  "0x1b58": { token: "ZETA", gasUnit: "Gwei", name: "ZetaChain" },
  "0x82750": { token: "ETH", gasUnit: "Gwei", name: "Scroll" },
  "0x4e454152": { token: "ETH", gasUnit: "Gwei", name: "Aurora" },
  "0x531": { token: "SEI", gasUnit: "Gwei", name: "Sei" },
  "0x3e7": { token: "HYPE", gasUnit: "Gwei", name: "HyperEVM" },
  "0xdef1": { token: "ETH", gasUnit: "Gwei", name: "Ink" },
  "0x3e6": { token: "HYPE", gasUnit: "Gwei", name: "HyperEVM Testnet" },
  "0x8f": { token: "MON", gasUnit: "Gwei", name: "Monad" },
  "0x3e9": { token: "KLAY", gasUnit: "Gwei", name: "Klaytn Baobab" },
  "0x4e454153": { token: "ETH", gasUnit: "Gwei", name: "Aurora Testnet" },
  "0x8274f": { token: "ETH", gasUnit: "Gwei", name: "Scroll Sepolia" },
  "0x530": { token: "SEI", gasUnit: "Gwei", name: "Sei Testnet" },
  "0x2eb": { token: "FLOW", gasUnit: "FLOW units", name: "Flow" },
  "0x7e4": { token: "RON", gasUnit: "Gwei", name: "Ronin" },
  "0x46f": { token: "LSK", gasUnit: "LSK units", name: "Lisk" },
  "0xaa36a7": { token: "ETH", gasUnit: "Gwei", name: "Sepolia" },
  "0x4268": { token: "ETH", gasUnit: "Gwei", name: "Holesky" },
  "0x13882": { token: "MATIC", gasUnit: "Gwei", name: "Polygon Amoy" },
  "0x61": { token: "tBNB", gasUnit: "Gwei", name: "BSC Testnet" },
  "0x66eee": { token: "ETH", gasUnit: "Gwei", name: "Arbitrum Sepolia" },
  "0x14a34": { token: "ETH", gasUnit: "Gwei", name: "Base Sepolia" },
  "0xaa37dc": { token: "ETH", gasUnit: "Gwei", name: "Optimism Sepolia" },
  "0xe705": { token: "ETH", gasUnit: "Gwei", name: "Linea Sepolia" },
  "0xfa2": { token: "FTM", gasUnit: "Gwei", name: "Fantom Testnet" },
  "0x27d8": { token: "xDAI", gasUnit: "Gwei", name: "Gnosis Chiado" },
  "0x15b32": { token: "CHZ", gasUnit: "Gwei", name: "Chiliz Testnet" },
  "0x507": { token: "DEV", gasUnit: "Gwei", name: "Moonbase" },
  "0xa0c71fd": { token: "ETH", gasUnit: "Gwei", name: "Blast Sepolia" },
  "0x12c": { token: "ETH", gasUnit: "Gwei", name: "zkSync Sepolia" },
  "0x138b": { token: "MNT", gasUnit: "Gwei", name: "Mantle Sepolia" },
  "0x98a": { token: "MATIC", gasUnit: "Gwei", name: "Polygon zkEVM Cardona" },
  "0x1b59": { token: "ZETA", gasUnit: "Gwei", name: "Zetachain Testnet" },
  "0x221": { token: "FLOW", gasUnit: "FLOW units", name: "Flow Testnet" },
  "0x7e5": { token: "RON", gasUnit: "Gwei", name: "Ronin Saigon Testnet" },
  "0x106a": { token: "LSK", gasUnit: "LSK units", name: "Lisk Sepolia" },
  "0x0": { token: "BTC", gasUnit: "Sats/byte", name: "Bitcoin" },
  "0x1399c": { token: "SOL", gasUnit: "Lamports", name: "Solana" },
  "0x162": { token: "DOT", gasUnit: "Plancks", name: "Polkadot" },
  "0x2611": { token: "XPL", gasUnit: "Gwei", name: "Plasma" },
  // Without a row, `seedFromCanonical` names a chain by its slug ("celo") and
  // gives it ETH. Every seeded chain has one; the rows below were added in
  // `_seed_data.py` first and ported, and the parity test keeps them in step.
  "0xa4ec": { token: "CELO", gasUnit: "Gwei", name: "Celo" },
  "0x440": { token: "METIS", gasUnit: "Gwei", name: "Metis" },
  "0x8ae": { token: "KAVA", gasUnit: "Gwei", name: "Kava" },
  "0x63564c40": { token: "ONE", gasUnit: "Gwei", name: "Harmony" },
  "0x7a": { token: "FUSE", gasUnit: "Gwei", name: "Fuse" },
  "0x2019": { token: "KLAY", gasUnit: "Gwei", name: "Klaytn" },
  "0x2a15c308d": { token: "PALM", gasUnit: "Gwei", name: "Palm" },
  "0x5": { token: "ETH", gasUnit: "Gwei", name: "Goerli" },
  "0x13881": { token: "MATIC", gasUnit: "Gwei", name: "Polygon Mumbai" },
  "0x14a33": { token: "ETH", gasUnit: "Gwei", name: "Base Goerli" },
  "0x1a4": { token: "ETH", gasUnit: "Gwei", name: "Optimism Goerli" },
  "0xaef3": { token: "CELO", gasUnit: "Gwei", name: "Celo Alfajores" },
  "0x8ad": { token: "TKAVA", gasUnit: "Gwei", name: "Kava Testnet" },
  "0x24c": { token: "METIS", gasUnit: "Gwei", name: "Metis Goerli" },
  "0x6357d2e0": { token: "ONE", gasUnit: "Gwei", name: "Harmony Testnet" },
  "0x118": { token: "ETH", gasUnit: "Gwei", name: "zkSync Goerli" },
  "0x7b": { token: "SPARK", gasUnit: "Gwei", name: "Fuse Sparknet" },
  "0xe704": { token: "ETH", gasUnit: "Gwei", name: "Linea Goerli" }
};
var EXPLORER_URLS = {
  eth: "https://etherscan.io",
  etc: "https://blockscout.com/etc/mainnet",
  polygon: "https://polygonscan.com",
  bsc: "https://bscscan.com",
  avalanche: "https://snowtrace.io",
  fantom: "https://ftmscan.com",
  arbitrum: "https://arbiscan.io",
  optimism: "https://optimistic.etherscan.io",
  zksync_era: "https://explorer.zksync.io",
  base: "https://basescan.org",
  linea: "https://lineascan.build",
  scroll: "https://scrollscan.com",
  mantle: "https://explorer.mantle.xyz",
  // The EVM-side Blockscout. Nesa also runs a Cosmos explorer at
  // `explorer.nesa.ai`, which cannot resolve an `0x` address or tx hash.
  nesa: "https://explorer-evm.nesa.ai",
  // Subscan, whose EVM mode answers the default /tx, /address, /block and
  // /token paths (each checked against a live peaq tx on 2026-09-16).
  peaq: "https://peaq.subscan.io",
  opbnb: "https://opbnbscan.com",
  polygon_zkevm: "https://zkevm.polygonscan.com",
  blast: "https://blastscan.io",
  pulsechain: "https://scan.pulsechain.com",
  aurora: "https://explorer.aurora.dev",
  flow: "https://evm.flowscan.io",
  ronin: "https://app.roninchain.com",
  lisk: "https://blockscout.lisk.com",
  zetachain: "https://explorer.zetachain.com",
  sei: "https://seitrace.com",
  hyperevm: "https://hyperevmscan.io",
  ink: "https://explorer.inkonchain.com",
  monad: "https://monadexplorer.com",
  plasma: "https://plasmascan.to",
  // --- Testnets ---
  // A testnet with no row here gets NO explorer link — the registry does not
  // lend it its mainnet's, which would show a different chain's object under
  // the same hash or address. So a row belongs here only when the testnet has
  // its own explorer that is live; these were checked on 2026-09-16 (DNS, the
  // site answering, and the chain id where the explorer exposes one).
  //
  // Deliberately absent, because the explorer is gone or now serves another
  // network: holesky (holesky.etherscan.io no longer resolves), mumbai
  // (mumbai.polygonscan.com no longer resolves — the Python table still carries
  // it), base_goerli, optimism_goerli, linea_testnet, zkSync_testnet,
  // metis_goerli, polygon_zkevm_cardona, fantom_testnet, celo_alfajores (both
  // its explorers redirect to Celo Sepolia), scroll_testnet
  // (sepolia.scrollscan.com is a Cloudflare 1014), lisk_sepolia (unreachable)
  // and sei_testnet (Seitrace picks the network by query string, which the link
  // builder cannot express).
  //
  // FE-only: the Python table has no mainnet fallback to correct, and feeds
  // scraping hosts, so these are not mirrored there.
  goerli: "https://goerli.etherscan.io",
  sepolia: "https://sepolia.etherscan.io",
  polygon_amoy: "https://amoy.polygonscan.com",
  bsc_testnet: "https://testnet.bscscan.com",
  gnosis_chiado: "https://gnosis-chiado.blockscout.com",
  base_sepolia: "https://sepolia.basescan.org",
  optimism_sepolia: "https://sepolia-optimism.etherscan.io",
  arbitrum_sepolia: "https://sepolia.arbiscan.io",
  aurora_testnet: "https://explorer.testnet.aurora.dev",
  moonbase: "https://moonbase.moonscan.io",
  fuse_testnet: "https://explorer.fusespark.io",
  linea_sepolia: "https://sepolia.lineascan.build",
  blast_sepolia: "https://sepolia.blastscan.io",
  mantle_sepolia: "https://sepolia.mantlescan.xyz",
  zksync_sepolia: "https://sepolia.explorer.zksync.io",
  flow_testnet: "https://testnet.evm.flow.com",
  ronin_saigon: "https://saigon-app.roninchain.com",
  zetachain_testnet: "https://testnet.zetascan.com"
};
var TESTNET_TO_MAINNET = {
  goerli: "eth",
  sepolia: "eth",
  mumbai: "polygon",
  bsc_testnet: "bsc",
  gnosis_chiado: "gnosis",
  chiliz_spicy: "chiliz",
  base_goerli: "base",
  optimism_goerli: "optimism",
  klaytn_baobab: "klaytn",
  celo_alfajores: "celo",
  kava_testnet: "kava",
  metis_goerli: "metis",
  harmony_testnet: "harmony",
  zkSync_testnet: "zkSync_era",
  aurora_testnet: "aurora",
  moonbase: "moonbeam",
  fuse_testnet: "fuse",
  linea_testnet: "linea",
  scroll_testnet: "scroll",
  holesky: "eth",
  polygon_amoy: "polygon",
  base_sepolia: "base",
  optimism_sepolia: "optimism",
  arbitrum_sepolia: "arbitrum",
  linea_sepolia: "linea",
  blast_sepolia: "blast",
  mantle_sepolia: "mantle",
  zksync_sepolia: "zkSync_era",
  polygon_zkevm_cardona: "polygon_zkevm",
  fantom_testnet: "fantom",
  lisk_sepolia: "lisk",
  flow_testnet: "flow",
  ronin_saigon: "ronin",
  zetachain_testnet: "zetachain",
  sei_testnet: "sei",
  hyperevm_testnet: "hyperevm"
};
var CHAIN_ALIASES = {
  "eth": ["ethereum", "ethereum-mainnet", "eth-mainnet"],
  "polygon": ["matic", "matic-network", "polygon-mainnet", "polygon-pos"],
  "bsc": ["binance", "binance-smart-chain", "bnb", "bnb-chain", "bnb-smart-chain"],
  "avalanche": ["avax", "avalanche-c-chain", "avax-c-chain", "avalanche-mainnet"],
  "arbitrum": ["arbitrum-one", "arb", "arbitrum-mainnet"],
  "optimism": ["op", "op-mainnet", "optimism-mainnet"],
  "base": ["base-mainnet"],
  "gnosis": ["xdai", "gnosis-chain"],
  "fantom": ["ftm", "fantom-opera"],
  "cronos": ["cro"],
  "linea": ["linea-mainnet"],
  "scroll": ["scroll-mainnet"],
  "blast": ["blast-mainnet"],
  "ronin": ["ron"],
  "sei": ["sei-mainnet"],
  "pulsechain": ["pls"],
  "polygon_zkevm": ["polygon-zkevm", "zkevm"],
  "zksync_era": ["zksync", "zksync-era", "era"],
  "mantle_sepolia": ["mantle-testnet", "mantle_testnet"],
  // Non-EVM — the short tickers callers reach for.
  "solana": ["sol"],
  "bitcoin": ["btc"],
  "polkadot": ["dot"],
  "hypercore": ["hyperliquid", "hyperliquid-core", "hyperliquid-l1"],
  "hyperevm": ["hyper-evm", "hyperliquid-evm"],
  "hypercore-testnet": ["hyperliquid-testnet", "hypercore_testnet"],
  "hyperevm_testnet": ["hyperevm-testnet", "hyperliquid-evm-testnet"],
  "monero": ["xmr"],
  "zcash": ["zec"]
};

// src/chains/registry.ts
function foldAliases(...groups) {
  const seen = /* @__PURE__ */ new Set();
  for (const group of groups) {
    for (const raw of group ?? []) {
      const alias = raw.trim().toLowerCase();
      if (alias) seen.add(alias);
    }
  }
  return [...seen];
}
var ChainRegistry = class {
  bySlug = /* @__PURE__ */ new Map();
  byHex = /* @__PURE__ */ new Map();
  byCaip2 = /* @__PURE__ */ new Map();
  byAlias = /* @__PURE__ */ new Map();
  register(chain, { replace = false } = {}) {
    const existingSlug = this.bySlug.get(chain.slug);
    if (existingSlug && existingSlug !== chain && !replace) {
      throw new Error(
        `Chain slug "${chain.slug}" already registered; pass replace=true to overwrite.`
      );
    }
    if (chain.chainIdHex) {
      const existingHex = this.byHex.get(chain.chainIdHex);
      if (existingHex && existingHex.slug !== chain.slug && !replace) {
        throw new Error(
          `Chain hex ${chain.chainIdHex} already mapped to slug ${existingHex.slug}; pass replace=true to overwrite.`
        );
      }
    }
    if (existingSlug) this.dropAliases(existingSlug);
    this.bySlug.set(chain.slug, chain);
    if (chain.chainIdHex) this.byHex.set(chain.chainIdHex, chain);
    if (chain.caip2) this.byCaip2.set(chain.caip2, chain);
    for (const alias of foldAliases(chain.aliases)) this.byAlias.set(alias, chain);
  }
  dropAliases(chain) {
    for (const alias of foldAliases(chain.aliases)) {
      if (this.byAlias.get(alias) === chain) this.byAlias.delete(alias);
    }
  }
  unregister(slug) {
    const chain = this.bySlug.get(slug);
    if (!chain) return null;
    this.bySlug.delete(slug);
    if (chain.chainIdHex) this.byHex.delete(chain.chainIdHex);
    if (chain.caip2) this.byCaip2.delete(chain.caip2);
    this.dropAliases(chain);
    return chain;
  }
  clear() {
    this.bySlug.clear();
    this.byHex.clear();
    this.byCaip2.clear();
    this.byAlias.clear();
  }
  get(slug) {
    return this.bySlug.get(slug) ?? null;
  }
  /** Lookup by EVM hex chain id (auto `0x`-prefixes + lowercases). */
  getByHex(chainIdHex) {
    let normalized = chainIdHex.toLowerCase();
    if (!normalized.startsWith("0x")) normalized = "0x" + normalized.replace(/^0x/, "");
    return this.byHex.get(normalized) ?? null;
  }
  /** Lookup by CAIP-2 chain id (e.g. "eip155:1"). */
  getByCaip2(caip2) {
    return this.byCaip2.get(normalizeCaip2(caip2)) ?? null;
  }
  /**
   * Resolve by CAIP-2, hex, bare decimal, slug, or alias — in that order, the
   * same order as `ChainRegistry.resolve` in `commons/chains/registry.py`, so a
   * spelling resolves to the same chain on both sides of the wire.
   */
  resolve(value) {
    if (!value) return null;
    if (value.includes(":")) {
      const hit = this.getByCaip2(value);
      if (hit) return hit;
    }
    if (value.startsWith("0x")) {
      const hit = this.getByHex(value);
      if (hit) return hit;
    }
    if (/^\d+$/.test(value)) {
      const hit = this.getByHex("0x" + BigInt(value).toString(16));
      if (hit) return hit;
    }
    const folded = value.toLowerCase();
    return this.get(folded) ?? this.byAlias.get(folded) ?? null;
  }
  byFamily(family) {
    return [...this.bySlug.values()].filter((c) => c.family === family).sort((a, b) => a.slug.localeCompare(b.slug));
  }
  all() {
    return [...this.bySlug.values()].sort((a, b) => a.slug.localeCompare(b.slug));
  }
};
var registry = new ChainRegistry();
function normalizeCaip2(value) {
  const split = value.trim().indexOf(":");
  if (split < 0) return value.trim();
  const namespace = value.trim().slice(0, split).toLowerCase();
  let reference = value.trim().slice(split + 1);
  if (["bip122", "polkadot"].includes(namespace)) reference = reference.toLowerCase();
  if (namespace === "eip155" && /^\d+$/.test(reference)) reference = BigInt(reference).toString();
  return `${namespace}:${reference}`;
}
function getChainMetadata(chainId) {
  return registry.resolve(chainId);
}
var NEUTRAL_CHAIN_COLOR = "#64748b";
function hexToRgba(hex, alpha) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const int = parseInt(m[1], 16);
  const r = int >> 16 & 255;
  const g = int >> 8 & 255;
  const b = int & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
var GROUP_FILL_ALPHA = 0.06;
var GROUP_BORDER_ALPHA = 0.45;
function brandGroupTint(brandColor) {
  return {
    fill: hexToRgba(brandColor, GROUP_FILL_ALPHA),
    border: hexToRgba(brandColor, GROUP_BORDER_ALPHA),
    text: brandColor
  };
}
function getChainGroupColors(chainId) {
  const chain = getChainMetadata(chainId);
  const brandColor = chain?.brandColor ?? NEUTRAL_CHAIN_COLOR;
  return {
    slug: chain?.slug ?? null,
    name: chain?.name ?? (chainId ? String(chainId) : "Unknown chain"),
    brandColor,
    ...brandGroupTint(brandColor)
  };
}
var REORG_DEPTHS_BY_SLUG = {
  eth: 12,
  polygon: 256,
  matic: 256,
  bsc: 15,
  arbitrum: 12,
  optimism: 12,
  base: 12,
  avalanche: 12,
  fantom: 12,
  cronos: 12,
  gnosis: 12,
  linea: 12,
  scroll: 12,
  mantle: 12,
  zksync: 12,
  zksync_era: 12,
  metis: 12,
  celo: 12,
  klaytn: 12,
  moonbeam: 12,
  moonriver: 12,
  harmony: 12,
  aurora: 12,
  fuse: 12,
  palm: 12,
  chiliz: 12,
  kava: 12,
  blast: 12,
  bitcoin: 6,
  solana: 32,
  // Monero's ten-block spend lock is a network rule; Zcash needs many more
  // blocks than Bitcoin for equal work at a 75-second block.
  monero: 10,
  zcash: 24,
  // HyperBFT commits a block final.
  hypercore: 1,
  hyperevm: 1,
  hyperevm_testnet: 1
};
var BLOCK_TIME_BY_SLUG = {
  eth: 12,
  etc: 13,
  polygon: 2,
  matic: 2,
  bsc: 3,
  arbitrum: 0.25,
  optimism: 2,
  base: 2,
  avalanche: 2,
  fantom: 1,
  cronos: 6,
  gnosis: 5,
  linea: 3,
  scroll: 3,
  mantle: 2,
  zksync: 1,
  zksync_era: 1,
  metis: 4,
  celo: 5,
  klaytn: 1,
  moonbeam: 12,
  moonriver: 12,
  harmony: 2,
  aurora: 1,
  fuse: 5,
  blast: 2,
  bitcoin: 600,
  solana: 0.4,
  polkadot: 6,
  monero: 120,
  zcash: 75,
  hypercore: 0.2,
  "hypercore-testnet": 0.2,
  hyperevm: 1
};
var EXPLORER_SUPPLEMENT = {
  bitcoin: "https://blockstream.info",
  solana: "https://solscan.io",
  polkadot: "https://polkadot.subscan.io",
  monero: "https://xmrchain.net",
  // Blockchair's base carries the coin as a path segment, which is exactly
  // what the link builder appends to — `/zcash` + `/transaction/<hash>`.
  zcash: "https://blockchair.com/zcash",
  // app.hyperliquid.xyz/explorer/tx/<hash>, /address/<addr>, /block/<n>.
  hypercore: "https://app.hyperliquid.xyz/explorer",
  "hypercore-testnet": "https://app.hyperliquid-testnet.xyz/explorer",
  cronos: "https://cronoscan.com",
  gnosis: "https://gnosisscan.io",
  celo: "https://celoscan.io",
  moonbeam: "https://moonscan.io",
  zkSync_era: "https://explorer.zksync.io",
  matic: "https://polygonscan.com"
};
function explorerFor(slug, rawSlug) {
  return EXPLORER_URLS[slug] ?? EXPLORER_URLS[rawSlug] ?? EXPLORER_SUPPLEMENT[slug] ?? EXPLORER_SUPPLEMENT[rawSlug] ?? null;
}
var BRAND_COLOR_BY_SLUG = {
  eth: "#627EEA",
  etc: "#328332",
  polygon: "#8247E5",
  matic: "#8247E5",
  bsc: "#F0B90B",
  arbitrum: "#28A0F0",
  optimism: "#FF0420",
  base: "#0052FF",
  avalanche: "#E84142",
  fantom: "#1969FF",
  cronos: "#002D74",
  gnosis: "#0B6E4F",
  linea: "#61DFFF",
  scroll: "#EBC28E",
  mantle: "#000000",
  zksync: "#8C8DFC",
  zksync_era: "#8C8DFC",
  metis: "#00DACC",
  celo: "#FCFF52",
  klaytn: "#FF1B1B",
  moonbeam: "#53CBC8",
  moonriver: "#C0C0C0",
  harmony: "#00AEE9",
  aurora: "#70D44B",
  fuse: "#B4F9BA",
  blast: "#FCFC03",
  kava: "#FF433E",
  bitcoin: "#F7931A",
  solana: "#14F195",
  polkadot: "#E6007A",
  monero: "#FF6600",
  zcash: "#F4B728",
  hypercore: "#97FCE4",
  hyperevm: "#97FCE4"
};
function brandColorFor(slug, mainnetSlug) {
  return BRAND_COLOR_BY_SLUG[slug] ?? (mainnetSlug ? BRAND_COLOR_BY_SLUG[mainnetSlug] ?? null : null);
}
var LOGO_URL_BY_SLUG = {};
var CAIP2_BY_SLUG = {
  "bitcoin-testnet": "bip122:000000000933ea01ad0ee984209779ba",
  "solana-devnet": "solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1",
  "solana-testnet": "solana:4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z",
  bitcoin: "bip122:000000000019d6689c085ae165831e93",
  solana: "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
  polkadot: "polkadot:91b171bb158e2d3848fa23a9f1c25182",
  // Zcash is a Bitcoin fork, so `bip122` is its registered namespace and the
  // reference is its genesis hash truncated to 32 chars — Bitcoin's own rule.
  zcash: "bip122:00040fe8ec8471911baa1db1266ea15d",
  // Monero has no CASA-registered namespace; `monero` is a platform-local
  // choice. See the note on the Python side, which this table must match
  // byte for byte — `test_chains_fe_registry_parity.py` fails if it drifts.
  monero: "monero:418015bb9ae982a1975da7d79277c270",
  // HyperCore has no CASA namespace, and `hyperliquid` / `hypercore` exceed
  // CAIP-2's eight-character namespace limit. Platform-local; matches Python.
  hypercore: "hyperliq:mainnet",
  "hypercore-testnet": "hyperliq:testnet"
};
function deriveCaip2(slug, family, chainIdHex) {
  if (CAIP2_BY_SLUG[slug]) return CAIP2_BY_SLUG[slug];
  if (family === ChainFamily.EVM && chainIdHex) {
    return `eip155:${BigInt(chainIdHex).toString(10)}`;
  }
  return null;
}
var NON_EVM_OVERRIDES = {
  "bitcoin-testnet": { family: ChainFamily.UTXO, decimals: 8, chainIdHex: null, name: "Bitcoin Testnet", nativeToken: "BTC", mainnetSlug: "bitcoin" },
  "solana-devnet": { family: ChainFamily.SOLANA, decimals: 9, chainIdHex: null, name: "Solana Devnet", nativeToken: "SOL", mainnetSlug: "solana" },
  "solana-testnet": { family: ChainFamily.SOLANA, decimals: 9, chainIdHex: null, name: "Solana Testnet", nativeToken: "SOL", mainnetSlug: "solana" },
  "hypercore-testnet": { family: ChainFamily.HYPERCORE, decimals: 8, chainIdHex: null, name: "HyperCore Testnet", nativeToken: "HYPE", mainnetSlug: "hypercore" },
  bitcoin: { family: ChainFamily.UTXO, decimals: 8, chainIdHex: null, name: "Bitcoin", nativeToken: "BTC", aliases: ["bitcoin-mainnet", "0x0"] },
  solana: { family: ChainFamily.SOLANA, decimals: 9, chainIdHex: null, name: "Solana", nativeToken: "SOL", aliases: ["solana-mainnet", "0x1399c"] },
  polkadot: { family: ChainFamily.OTHER, decimals: 10, chainIdHex: null, name: "Polkadot", nativeToken: "DOT", aliases: ["polkadot-mainnet", "0x162"] },
  monero: { family: ChainFamily.UTXO, decimals: 12, chainIdHex: null, name: "Monero", nativeToken: "XMR", aliases: ["monero-mainnet"] },
  zcash: { family: ChainFamily.UTXO, decimals: 8, chainIdHex: null, name: "Zcash", nativeToken: "ZEC", aliases: ["zcash-mainnet"] },
  hypercore: { family: ChainFamily.HYPERCORE, decimals: 8, chainIdHex: null, name: "HyperCore", nativeToken: "HYPE", aliases: ["hypercore-mainnet"] }
};
function layerFor(slug) {
  const folded = slug.toLowerCase();
  const listedIn = (listed) => listed.some((member) => member.toLowerCase() === folded);
  if (listedIn(LAYER1_CHAINS)) return "l1";
  if (listedIn(LAYER2_CHAINS)) return "l2";
  if (listedIn(SIDECHAINS)) return "sidechain";
  return "unknown";
}
function seedFromCanonical() {
  for (const [hexId, rawSlug] of Object.entries(CHAIN_MAPPING)) {
    const slug = rawSlug.toLowerCase();
    const config = CHAIN_CONFIG[hexId];
    const token = config?.token ?? "ETH";
    const override = NON_EVM_OVERRIDES[slug];
    const family = override?.family ?? ChainFamily.EVM;
    const decimals = override?.decimals ?? 18;
    const isTestnet = TESTNET_CHAINS.includes(rawSlug);
    let mainnetSlug = null;
    if (isTestnet) {
      const m = TESTNET_TO_MAINNET[rawSlug];
      if (m) mainnetSlug = m.toLowerCase();
    }
    const reorgDepth = REORG_DEPTHS_BY_SLUG[slug] ?? 12;
    const chainIdHex = hexId.toLowerCase();
    const metadata = { layer: layerFor(slug) };
    if (config?.gasUnit) metadata.gasUnit = config.gasUnit;
    if (slug === "etc") {
      metadata.fork_of = "eth";
      metadata.note = "ETC shares pre-fork tx hashes with ETH. Audit sec. 2 calls out this collision risk.";
    }
    try {
      registry.register(
        {
          slug,
          chainIdHex,
          family,
          name: config?.name ?? rawSlug,
          nativeToken: token,
          decimals,
          isTestnet,
          mainnetSlug,
          reorgDepth,
          explorerUrl: explorerFor(slug, rawSlug),
          blockTimeSec: BLOCK_TIME_BY_SLUG[slug] ?? (mainnetSlug ? BLOCK_TIME_BY_SLUG[mainnetSlug] ?? null : null),
          caip2: deriveCaip2(slug, family, chainIdHex),
          brandColor: brandColorFor(slug, mainnetSlug),
          logoUrl: LOGO_URL_BY_SLUG[slug] ?? (mainnetSlug ? LOGO_URL_BY_SLUG[mainnetSlug] ?? null : null),
          metadata,
          aliases: foldAliases(override?.aliases, CHAIN_ALIASES[slug])
        },
        { replace: true }
      );
    } catch {
      continue;
    }
  }
  for (const [slug, ovr] of Object.entries(NON_EVM_OVERRIDES)) {
    if (registry.get(slug)) continue;
    try {
      registry.register(
        {
          slug,
          chainIdHex: ovr.chainIdHex,
          family: ovr.family,
          name: ovr.name,
          nativeToken: ovr.nativeToken,
          decimals: ovr.decimals,
          isTestnet: !!ovr.mainnetSlug,
          mainnetSlug: ovr.mainnetSlug ?? null,
          reorgDepth: REORG_DEPTHS_BY_SLUG[slug] ?? REORG_DEPTHS_BY_SLUG[ovr.mainnetSlug ?? ""] ?? 12,
          explorerUrl: explorerFor(slug, slug),
          blockTimeSec: BLOCK_TIME_BY_SLUG[slug] ?? null,
          caip2: deriveCaip2(slug, ovr.family, ovr.chainIdHex),
          brandColor: brandColorFor(slug, null),
          logoUrl: LOGO_URL_BY_SLUG[slug] ?? null,
          metadata: {},
          aliases: foldAliases(ovr.aliases, CHAIN_ALIASES[slug])
        },
        { replace: true }
      );
    } catch {
      continue;
    }
  }
}
seedFromCanonical();

// src/chains/tx.ts
var RE_HEX64 = /^(0x)?[0-9a-fA-F]{64}$/i;
var RE_SOLANA_SIG = /^[1-9A-HJ-NP-Za-km-z]{64,88}$/;
function normalizeTxHash(txHash, chainId) {
  const h = txHash.trim();
  const fam = chainId ? getChainMetadata(chainId)?.family ?? null : null;
  if (fam === ChainFamily.SOLANA || !RE_HEX64.test(h) && RE_SOLANA_SIG.test(h)) {
    return h;
  }
  if (RE_HEX64.test(h)) {
    const bare = h.replace(/^0x/i, "").toLowerCase();
    if (fam === ChainFamily.UTXO) return bare;
    return `0x${bare}`;
  }
  return h;
}
function crossChainGroupingKey(input) {
  const chain = getChainMetadata(input.chainId);
  const chainKey = chain?.slug ?? (input.chainId ?? "unknown").toString().toLowerCase();
  return `${chainKey}:${normalizeTxHash(input.txHash, input.chainId)}`;
}

// src/transpose/transaction-row.ts
function unwrapTxEnvelope(entry) {
  const inner = entry?.transaction;
  return inner && typeof inner === "object" ? inner : entry ?? {};
}
function txEntryUniqueAddresses(entry) {
  const list = entry?.unique_addresses;
  return Array.isArray(list) ? list.filter((a) => typeof a === "string") : [];
}
function coerceTransactionRow(raw) {
  const tx = unwrapTxEnvelope(raw);
  const get = (...keys) => {
    for (const k of keys) {
      const v = tx[k];
      if (v != null && v !== "") return v;
    }
    return void 0;
  };
  const ps = tx.participant_summary ?? {};
  const body = tx.body ?? {};
  const nonEmpty = (v) => v != null && v !== "" ? String(v) : void 0;
  const chainId = String(get("chain_id", "chain") ?? "");
  const txHash = String(get("tx_hash", "transaction_hash", "hash") ?? "");
  const canonicalTxId = String(
    get("canonical_tx_id") ?? (chainId && txHash ? crossChainGroupingKey({ chainId, txHash }) : txHash)
  );
  const tsRaw = get("block_timestamp", "timestamp");
  const blockTimestamp = typeof tsRaw === "number" ? tsRaw : Number(tsRaw ?? 0) || 0;
  return {
    ...tx,
    canonical_tx_id: canonicalTxId,
    chain_id: chainId,
    tx_hash: txHash,
    block_timestamp: blockTimestamp,
    total_value_raw: String(get("total_value_raw", "value") ?? "0"),
    type: get("type") ?? "transaction",
    finality: get("finality") ?? "unknown",
    from: get("from", "from_address") ?? nonEmpty(ps.primary_from) ?? nonEmpty(body.from_address) ?? null,
    to: get("to", "to_address") ?? nonEmpty(ps.primary_to) ?? nonEmpty(body.to_address) ?? null,
    primary_entity_id: get("primary_entity_id") ?? null
    // The feed/list projection is a small subset of the generated row contract
    // (which marks the tenant-scoping keys required); the producer fields ride
    // along on `...tx`. Cast through `unknown` — the shape is intentionally partial.
  };
}
function coerceTransactionRows(raws) {
  return raws.map(coerceTransactionRow);
}

// src/transpose/addresses.ts
function counterIndex(graph) {
  const index = /* @__PURE__ */ new Map();
  if (!graph) return index;
  for (const node of graph.nodes ?? []) {
    if (node && typeof node.address === "string") index.set(node.address, node);
  }
  return index;
}
function toUniqueAddresses(addresses, options = {}) {
  const counters = counterIndex(options.graph);
  const out = [];
  for (const address of addresses) {
    if (!address) continue;
    const node = counters.get(address);
    const overlay = options.resolveOverlay?.(address);
    out.push({
      address,
      chainId: options.chainId,
      degree: node?.degree,
      txCount: node?.tx_count,
      label: overlay?.label,
      primaryEntityId: overlay?.primaryEntityId,
      tags: overlay?.tags,
      color: overlay?.color,
      logo: overlay?.logo
    });
  }
  return out;
}
var SCENE_DETAIL_TIERS = ["compact", "standard", "expanded"];
var SCENE_EMPTY_REASON_ATTR = "empty_reason";
var MediaRefSchema = z.object({
  kind: z.string().default("placeholder"),
  value: z.string(),
  fallback: z.union([z.string(), z.null()]).default(null),
  alt: z.union([z.string(), z.null()]).default(null)
});
var SceneAssetSchema = z.object({
  token_address: z.union([z.string(), z.null()]).default(null),
  token_chain_id: z.union([z.string(), z.null()]).default(null),
  symbol: z.union([z.string(), z.null()]).default(null),
  decimals: z.union([z.number().int(), z.null()]).default(null),
  amount_raw: z.union([z.string(), z.null()]).default(null),
  amount: z.union([z.string(), z.null()]).default(null),
  usd: z.union([z.string(), z.null()]).default(null),
  direction: z.union([z.string(), z.null()]).default(null),
  // 'actual' | 'quoted_minimum' — mirrors commons AmountBasis. Lets a label
  // flag a swap min-return floor as "(min)" instead of an exact amount.
  basis: z.union([z.string(), z.null()]).default(null),
  min_amount_raw: z.union([z.string(), z.null()]).default(null),
  min_amount: z.union([z.string(), z.null()]).default(null),
  icon: z.union([MediaRefSchema, z.null()]).default(null)
});
var SceneSourceRefSchema = z.object({
  ref_type: z.string(),
  address: z.union([z.string(), z.null()]).default(null),
  log_index: z.union([z.number().int(), z.null()]).default(null),
  internal_tx_index: z.union([z.number().int(), z.null()]).default(null),
  path: z.array(z.string()).default([])
});
var SceneFactSchema = z.object({
  key: z.string(),
  label: z.string().default(""),
  kind: z.union([z.string(), z.null()]).default(null),
  value: z.unknown(),
  protocol_id: z.union([z.string(), z.null()]).default(null),
  source: z.union([z.string(), z.null()]).default(null),
  path_id: z.union([z.string(), z.null()]).default(null),
  confidence: z.union([z.number().min(0).max(1), z.null()]).default(null),
  icon: z.union([z.string(), z.null()]).default(null),
  description: z.union([z.string(), z.null()]).default(null)
});
var SceneNodeSchema = z.object({
  id: z.string(),
  kind: z.string().default("address"),
  label: z.string().default(""),
  sublabel: z.union([z.string(), z.null()]).default(null),
  chain_id: z.union([z.string(), z.null()]).default(null),
  address: z.union([z.string(), z.null()]).default(null),
  group_id: z.union([z.string(), z.null()]).default(null),
  off_chain: z.boolean().default(false),
  entity_id: z.union([z.string(), z.null()]).default(null),
  icon: z.union([MediaRefSchema, z.null()]).default(null),
  assets: z.array(SceneAssetSchema).default([]),
  value: z.union([z.string(), z.null()]).default(null),
  badge: z.union([z.string(), z.null()]).default(null),
  attributes: z.record(z.string(), z.unknown()).default({}),
  source_refs: z.array(SceneSourceRefSchema).default([]),
  confidence: z.number().min(0).max(1).default(1),
  detail_tier: z.string().default("compact"),
  facts: z.array(SceneFactSchema).default([]),
  origin: z.string().default("decoded")
});
var SceneEdgeSchema = z.object({
  id: z.string(),
  source: z.string(),
  target: z.string(),
  kind: z.string().default("flow"),
  label: z.union([z.string(), z.null()]).default(null),
  asset: z.union([SceneAssetSchema, z.null()]).default(null),
  direction: z.union([z.string(), z.null()]).default(null),
  from_chain: z.union([z.string(), z.null()]).default(null),
  to_chain: z.union([z.string(), z.null()]).default(null),
  cross_chain: z.boolean().default(false),
  virtual: z.boolean().default(false),
  icon: z.union([MediaRefSchema, z.null()]).default(null),
  attributes: z.record(z.string(), z.unknown()).default({}),
  detail_tier: z.string().default("compact"),
  facts: z.array(SceneFactSchema).default([]),
  origin: z.string().default("decoded")
});
var MERGED_COUNT_ATTR = "merged_count";
var MERGED_TXS_ATTR = "merged_txs";
function mergedTxRefs(attributes) {
  const raw = attributes?.[MERGED_TXS_ATTR];
  if (!Array.isArray(raw)) return [];
  const seen = /* @__PURE__ */ new Set();
  const refs = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const { id, tx_hash, chain_id, amount_raw } = entry;
    if (typeof id !== "string" || !id || seen.has(id)) continue;
    seen.add(id);
    refs.push({
      id,
      ...typeof tx_hash === "string" && tx_hash ? { tx_hash } : {},
      ...typeof chain_id === "string" && chain_id ? { chain_id } : {},
      ...typeof amount_raw === "string" && /^-?\d+$/.test(amount_raw) ? { amount_raw } : {}
    });
  }
  return refs;
}
function mergedCount(attributes) {
  const value = attributes?.[MERGED_COUNT_ATTR];
  return typeof value === "number" && value > 0 ? value : 1;
}
var SceneGroupSchema = z.object({
  id: z.string(),
  group_type: z.string().default("chain"),
  chain_id: z.union([z.string(), z.null()]).default(null),
  label: z.string().default(""),
  member_node_ids: z.array(z.string()).default([]),
  color: z.union([z.string(), z.null()]).default(null),
  icon: z.union([MediaRefSchema, z.null()]).default(null)
});
z.object({
  version: z.string().default("1"),
  kind: z.string().default("semantic"),
  summary: z.union([z.string(), z.null()]).default(null),
  nodes: z.array(SceneNodeSchema).default([]),
  edges: z.array(SceneEdgeSchema).default([]),
  groups: z.array(SceneGroupSchema).default([]),
  // The transaction's headline "business metrics" — the SAME resolved
  // `key_attributes` groups the detail-page highlights panel shows, carried on
  // EVERY chart kind so the graph view renders a consistent highlights strip.
  // Kept as the raw snake_case wire shape (a passthrough) so the wire layer
  // stays below `storage`; a consumer normalises it with `parseKeyAttributeGroups`
  // (the same parser the detail panel + group view use) → `KeyAttributeGroup[]`.
  highlights: z.array(z.record(z.string(), z.unknown())).default([]),
  chain_ids: z.array(z.string()).default([]),
  multi_chain: z.boolean().default(false),
  layout_hint: z.string().default("hierarchical"),
  // Compact-first: mirrors the backend `GraphBuildPolicy.default_view_tier`
  // default so a scene missing the field opens on the minimal intent + highlights
  // (the user raises the tier live). The scene always carries every tier.
  default_detail_tier: z.string().default("compact"),
  attributes: z.record(z.string(), z.unknown()).default({})
});
z.object({
  nodes: z.array(SceneNodeSchema).default([]),
  edges: z.array(SceneEdgeSchema).default([]),
  groups: z.array(SceneGroupSchema).default([]),
  hidden_node_ids: z.array(z.string()).default([]),
  hidden_edge_ids: z.array(z.string()).default([]),
  node_patches: z.record(z.string(), z.record(z.string(), z.unknown())).default({}),
  edge_patches: z.record(z.string(), z.record(z.string(), z.unknown())).default({}),
  /**
   * Where the author put an authored node. Honoured as a pin, not a hint, and
   * only for nodes the overlay itself added — pinning a decoded node freezes a
   * layout the backend is entitled to change, and the position was never the
   * author's to give.
   */
  positions: z.record(z.string(), z.object({ x: z.number(), y: z.number() })).default({}),
  /**
   * Who asserted this overlay, when, and why. Stamped by the service from the
   * caller's identity, never read from the element bodies — a client can no more
   * forge an author than it can forge `origin`.
   */
  authored_by: z.union([z.string(), z.null()]).default(null),
  authored_at: z.union([z.string(), z.null()]).default(null),
  authored_reason: z.union([z.string(), z.null()]).default(null),
  /**
   * The tier this overlay was published at, stamped by the read.
   *
   * On the BODY rather than left on the row, because the body is all a fold
   * receives — and without it a fold cannot tell one overlay overriding a lower
   * rung (precedence working) from two peers at the same rung contradicting each
   * other. Written by `overlay_wire` on the API and by the tx_graph store.
   */
  authored_scope: z.union([z.string(), z.null()]).default(null)
});
var CONTESTED_ATTR = "contested";
var GROUPING_DIMENSIONS = [
  "chain",
  "protocol",
  "entity",
  "tag",
  "label",
  "kind",
  "saga"
];
var GroupingDimensionOptionsSchema = z.object({
  /** Drop a band with fewer members than this (a band of one is just a node). */
  min_members: z.number().int().positive().optional(),
  /** Keep at most this many bands (largest first); the rest go ungrouped. */
  max_bands: z.number().int().positive().optional()
});
z.object({
  dimensions: z.array(z.enum(GROUPING_DIMENSIONS)).default([]),
  options: z.record(z.string(), GroupingDimensionOptionsSchema).default({})
});

// src/value/index.ts
function toBaseUnits(value) {
  if (typeof value === "bigint") return value;
  const text = value.trim();
  if (!/^[-+]?\d+$/.test(text)) return void 0;
  try {
    return BigInt(text);
  } catch {
    return void 0;
  }
}
function formatUnits(value, decimals) {
  const parsed = toBaseUnits(value);
  const scale = Number.isInteger(decimals) && decimals >= 0 ? decimals : void 0;
  if (parsed === void 0 || scale === void 0) {
    return typeof value === "string" ? value.trim() : String(value);
  }
  let v = parsed;
  const negative = v < 0n;
  if (negative) v = -v;
  const base = 10n ** BigInt(scale);
  const whole = v / base;
  const frac = v % base;
  if (scale === 0) return `${negative ? "-" : ""}${whole.toString()}`;
  const fracStr = frac.toString().padStart(scale, "0").replace(/0+$/, "");
  const body = fracStr ? `${whole.toString()}.${fracStr}` : whole.toString();
  return `${negative ? "-" : ""}${body}`;
}
function tokenToUsd(amount, decimals, priceUsd) {
  const human = Number(formatUnits(amount, decimals));
  if (!Number.isFinite(human) || !Number.isFinite(priceUsd)) return 0;
  return human * priceUsd;
}
var USD_FORMATTER = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
var SUB_CENT_USD = 5e-3;
function subCentUsdBound(value) {
  return value < 0 ? ">-$0.01" : "<$0.01";
}
function formatUsd(value, opts = {}) {
  if (value !== 0 && Number.isFinite(value) && Math.abs(value) < SUB_CENT_USD) {
    return subCentUsdBound(value);
  }
  if (opts.compact || Math.abs(value) >= 1e4) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      notation: "compact",
      maximumFractionDigits: 2
    }).format(value);
  }
  return USD_FORMATTER.format(value);
}
var SUB_PRECISION_SIGNIFICANT_DIGITS = 2;
var SUB_PRECISION_MAX_FRACTION = 12;
function subPrecisionAmount(whole, fraction, maxFrac) {
  const firstSignificant = fraction.search(/[1-9]/);
  const keep = firstSignificant + SUB_PRECISION_SIGNIFICANT_DIGITS;
  if (firstSignificant >= 0 && keep <= SUB_PRECISION_MAX_FRACTION) {
    const kept = fraction.slice(0, keep).replace(/0+$/, "");
    return `${whole}.${kept}`;
  }
  if (!isZeroText(whole)) return whole;
  const bound = `0.${"0".repeat(Math.max(maxFrac - 1, 0))}1`;
  return whole.startsWith("-") ? `>-${bound}` : `<${bound}`;
}
function formatTokenDisplay(amount, decimals, opts = {}) {
  const full = formatUnits(amount, decimals);
  const maxFrac = opts.maxFractionDigits ?? 6;
  let out = full;
  if (full.includes(".")) {
    const w = full.split(".")[0] ?? "0";
    const f = full.split(".")[1] ?? "";
    const kept = f.slice(0, maxFrac).replace(/0+$/, "");
    out = kept ? `${w}.${kept}` : subPrecisionAmount(w, f, maxFrac);
  }
  return opts.symbol ? `${out} ${opts.symbol}` : out;
}
function isZeroText(value) {
  return value === "0" || value === "-0";
}
var ELLIPSIS = "\u2026";
var DEFAULT_TRUNCATION = {
  hash: { head: 10, tail: 8 },
  address: { head: 6, tail: 4 }
};
var TRUNCATION_SLOT = /* @__PURE__ */ Symbol.for("@authe/fe-commons/display/truncation");
function activeTruncation() {
  return globalThis[TRUNCATION_SLOT] ?? DEFAULT_TRUNCATION;
}
function getTruncation(kind) {
  const active = activeTruncation();
  return kind === "hash" ? active.hash : active.address;
}
function hexPrefixOf(value) {
  return /^0x/i.test(value) ? value.slice(0, 2) : "";
}
function truncateMiddle(value, lengths) {
  const text = value.trim();
  if (/\s/.test(text)) return value;
  const prefix = hexPrefixOf(text);
  const body = text.slice(prefix.length);
  if (body.length <= lengths.head + lengths.tail + ELLIPSIS.length) return text;
  return `${prefix}${body.slice(0, lengths.head)}${ELLIPSIS}${body.slice(-lengths.tail)}`;
}
var BASE58 = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
var BECH32 = "qpzry9x8gf2tvdw0s3jn54khce6mua7l";
var GENERATORS = [996825010, 642813549, 513874426, 1027748829, 705979059];
var BITCOIN_ADDRESS_NETWORKS = {
  bitcoin: { hrp: "bc", versions: [0, 5] },
  "bitcoin-testnet": { hrp: "tb", versions: [111, 196] },
  "bitcoin-regtest": { hrp: "bcrt", versions: [111, 196] }
};
function decodeBase58(address) {
  if (!address || address.length > 90) return null;
  let value = 0n;
  for (const char of address) {
    const digit = BASE58.indexOf(char);
    if (digit < 0) return null;
    value = value * 58n + BigInt(digit);
  }
  const bytes = [];
  while (value) {
    bytes.push(Number(value & 255n));
    value >>= 8n;
  }
  for (const char of address) {
    if (char !== "1") break;
    bytes.push(0);
  }
  return Uint8Array.from(bytes.reverse());
}
var ZCASH_ADDRESS_NETWORKS = {
  zcash: { transparent: [[28, 184], [28, 189]], sapling: "zs", unified: "u" }
};
var MONERO_ADDRESS_NETWORKS = {
  monero: { 95: ["4", "8"], 106: ["4"] }
};
var BECH32_CONST = 1;
var BECH32M_CONST = 734539939;
var SAPLING_PAYLOAD_BYTES = 43;
function decodeBech32(address, maxLength = 90) {
  if (maxLength !== null && address.length > maxLength) return null;
  const lower = address.toLowerCase();
  if (address !== lower && address !== address.toUpperCase()) return null;
  const separator = lower.lastIndexOf("1");
  if (separator < 1) return null;
  const hrp = lower.slice(0, separator);
  const data = [...lower.slice(separator + 1)].map((c) => BECH32.indexOf(c));
  if (data.length < 7 || data.some((v) => v < 0)) return null;
  const expanded = [...hrp].map((c) => c.charCodeAt(0) >> 5).concat([0], [...hrp].map((c) => c.charCodeAt(0) & 31), data);
  let checksum = 1;
  for (const value of expanded) {
    const top = checksum >>> 25;
    checksum = (checksum & 33554431) << 5 ^ value;
    GENERATORS.forEach((generator, bit) => {
      if (top >>> bit & 1) checksum ^= generator;
    });
  }
  if (checksum !== BECH32_CONST && checksum !== BECH32M_CONST) return null;
  return { hrp, data: data.slice(0, -6), constant: checksum };
}
function convertedLength(data) {
  let bits = 0, accumulator = 0, length = 0;
  for (const value of data) {
    accumulator = (accumulator << 5 | value) & 4095;
    bits += 5;
    if (bits >= 8) {
      bits -= 8;
      length++;
    }
  }
  return bits < 5 && (accumulator << 8 - bits & 255) === 0 ? length : null;
}
function base58checkPayload(address) {
  const decoded = decodeBase58(address);
  if (!decoded || decoded.length < 5) return null;
  const checksum = sha256(sha256(decoded.slice(0, -4)));
  return decoded.slice(-4).every((value, index) => value === checksum[index]) ? decoded.slice(0, -4) : null;
}
function validBitcoinAddress(address, network) {
  if (address.length < 14 || address.length > 90) return false;
  const formats = Object.values(BITCOIN_ADDRESS_NETWORKS);
  if (!formats.length) return false;
  if (/^(bc1|tb1|bcrt1)/i.test(address)) {
    const decoded = decodeBech32(address);
    if (!decoded || !formats.some((format) => format.hrp === decoded.hrp) || !decoded.data.length) return false;
    const version = decoded.data[0];
    if (version > 16 || decoded.constant !== (version === 0 ? BECH32_CONST : BECH32M_CONST)) return false;
    const length = convertedLength(decoded.data.slice(1));
    return length !== null && length >= 2 && length <= 40 && (version !== 0 || length === 20 || length === 32);
  }
  const payload = base58checkPayload(address);
  return payload !== null && payload.length === 21 && formats.some((format) => format.versions.includes(payload[0]));
}
function validZcashAddress(address, network = "zcash") {
  const format = ZCASH_ADDRESS_NETWORKS[network];
  if (!format || !address) return false;
  if (address.startsWith("t")) {
    const payload = base58checkPayload(address);
    return payload !== null && payload.length === 22 && format.transparent.some(([a, b]) => payload[0] === a && payload[1] === b);
  }
  const unified = address.toLowerCase().startsWith(`${format.unified}1`);
  const decoded = decodeBech32(address, unified ? null : 90);
  if (!decoded) return false;
  if (decoded.hrp === format.sapling && decoded.constant === BECH32_CONST) {
    return convertedLength(decoded.data) === SAPLING_PAYLOAD_BYTES;
  }
  return decoded.hrp === format.unified && decoded.constant === BECH32M_CONST;
}
function validMoneroAddress(address, network = "monero") {
  const prefixes = MONERO_ADDRESS_NETWORKS[network]?.[address.length];
  return Boolean(prefixes?.some((prefix) => address.startsWith(prefix))) && [...address].every((char) => BASE58.includes(char));
}

// src/chains/address.ts
var RE_EVM = /^0x[0-9a-fA-F]{40}$/;
var RE_BTC_BECH32 = /^(bc1|tb1|bcrt1|zs1)[0-9ac-hj-np-z]{11,87}$/i;
var RE_SOLANA = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
var RE_COSMOS_BECH32 = /^[a-z][a-z0-9]{1,10}1[0-9ac-hj-np-z]{38,58}$/;
function addressFamily(address, chainId) {
  if (chainId) {
    const fam = getChainMetadata(chainId)?.family;
    if (fam) return fam;
  }
  if (RE_EVM.test(address)) return ChainFamily.EVM;
  if (validBitcoinAddress(address) || RE_BTC_BECH32.test(address) || validZcashAddress(address) || validMoneroAddress(address)) {
    return ChainFamily.UTXO;
  }
  if (RE_SOLANA.test(address)) return ChainFamily.SOLANA;
  if (RE_COSMOS_BECH32.test(address)) return ChainFamily.COSMOS;
  return null;
}
function normalizeAddress(address, chainId) {
  const addr = address.trim();
  const fam = addressFamily(addr, chainId);
  if (hasEvmIdentifiers(fam)) {
    const hex = addr.toLowerCase();
    return hex.startsWith("0x") ? hex : `0x${hex}`;
  }
  if (fam === null && /^0x/i.test(addr)) return addr.toLowerCase();
  if (fam === ChainFamily.UTXO && RE_BTC_BECH32.test(addr) && (addr === addr.toLowerCase() || addr === addr.toUpperCase())) return addr.toLowerCase();
  return addr;
}
function shortenAddress(address, lengths) {
  return truncateMiddle(address, { ...getTruncation("address"), ...lengths });
}
function shortenHash(hash, lengths) {
  return truncateMiddle(hash, { ...getTruncation("hash"), ...lengths });
}

// src/transpose/flows.ts
function tokenPriceFor(prices, tokenAddress, chainId) {
  if (!tokenAddress || !prices) return void 0;
  return prices[normalizeAddress(tokenAddress, chainId)] ?? prices[tokenAddress.toLowerCase()];
}
function priceFor(tokenAddress, prices) {
  return tokenPriceFor(prices, tokenAddress);
}
function resolveSymbol(tokenAddress, kind, price, options) {
  const explicit = options.resolveSymbol?.(tokenAddress);
  if (explicit) return explicit;
  if (price?.symbol) return price.symbol;
  if (!tokenAddress || !TOKEN_FLOW_KINDS.has(kind)) return options.nativeSymbol;
  return void 0;
}
function toTokenFlowRows(flows, options = {}) {
  return flows.map((flow) => {
    const price = priceFor(flow.token_address, options.prices);
    const decimals = flow.decimals ?? price?.decimals ?? void 0;
    const symbol = resolveSymbol(flow.token_address, flow.kind, price, options);
    const amountDisplay = decimals != null ? formatTokenDisplay(flow.raw_amount, decimals) : void 0;
    let valueUsd = flow.value_usd ?? void 0;
    if (valueUsd == null) {
      const unitPrice = flow.price_usd ?? price?.price_usd ?? void 0;
      if (decimals != null && unitPrice != null) {
        valueUsd = tokenToUsd(flow.raw_amount, decimals, unitPrice);
      }
    }
    return {
      kind: flow.kind,
      tokenAddress: flow.token_address ?? void 0,
      symbol,
      from: flow.from_address ?? void 0,
      to: flow.to_address ?? void 0,
      rawAmount: flow.raw_amount,
      amountDisplay,
      valueUsd,
      valueUsdDisplay: valueUsd != null ? formatUsd(valueUsd) : void 0,
      logIndex: flow.log_index ?? void 0
    };
  });
}
function directionOf(rawDelta) {
  try {
    const v = BigInt(rawDelta);
    if (v > 0n) return "in";
    if (v < 0n) return "out";
    return "flat";
  } catch {
    return "flat";
  }
}
function toNetFlowRows(netFlows, options = {}) {
  return netFlows.map((net) => {
    const price = priceFor(net.token_address, options.prices);
    const decimals = price?.decimals ?? void 0;
    const symbol = resolveSymbol(net.token_address, net.token_address ? "erc20" : "native", price, options);
    const deltaDisplay = decimals != null ? formatTokenDisplay(net.raw_delta, decimals) : void 0;
    const valueUsd = net.value_usd ?? void 0;
    return {
      address: net.address,
      tokenAddress: net.token_address ?? void 0,
      symbol,
      rawDelta: net.raw_delta,
      deltaDisplay,
      valueUsd,
      valueUsdDisplay: valueUsd != null ? formatUsd(valueUsd) : void 0,
      direction: directionOf(net.raw_delta)
    };
  });
}

// src/transpose/graph.ts
function nodeView(node, options) {
  const chainId = node.chain_id ?? options.chainId;
  const overlay = node.address ? options.resolveOverlay?.(node.address, chainId) : void 0;
  const id = node.id || node.address;
  const fallbackLabel = node.address ? shortenAddress(node.address) : id;
  return {
    id,
    address: node.address,
    degree: node.degree ?? 0,
    txCount: node.tx_count ?? 0,
    label: overlay?.label ?? node.label ?? fallbackLabel,
    chainId,
    nodeType: node.node_type ?? void 0,
    // Overlay brand/attribution (undefined when the address carries none).
    logo: overlay?.logo,
    color: overlay?.color,
    primaryEntityId: overlay?.primaryEntityId,
    tags: overlay?.tags && overlay.tags.length > 0 ? overlay.tags : void 0,
    // The overlay's weight, not only its name — the same pair the scene
    // transpose carries. A node whose label IS the accusation has to be able to
    // look like one, and `GraphNode`'s risk badge had nothing to read here.
    riskLevel: overlay?.riskLevel,
    riskScore: overlay?.riskScore
  };
}
function endpoint(id, address) {
  return id || address;
}
function graphEdgeId(edge) {
  const token = edge.token_address ?? "native";
  const kind = edge.kind ?? "";
  const from = endpoint(edge.from_id, edge.from_address);
  const to = endpoint(edge.to_id, edge.to_address);
  return `${from}->${to}:${token}:${kind}`;
}
function edgeView(edge, options) {
  const price = tokenPriceFor(
    options.prices,
    edge.token_address,
    edge.token_chain_id ?? edge.from_chain ?? options.chainId
  );
  const decimals = price?.decimals ?? void 0;
  const symbol = options.resolveSymbol?.(edge.token_address) ?? price?.symbol ?? void 0;
  const amountDisplay = decimals != null ? formatTokenDisplay(edge.raw_amount_total, decimals) : void 0;
  const valueUsd = decimals != null && price?.price_usd != null ? tokenToUsd(edge.raw_amount_total, decimals, price.price_usd) : void 0;
  const fromChain = edge.from_chain ?? void 0;
  const toChain = edge.to_chain ?? void 0;
  return {
    id: graphEdgeId(edge),
    from: endpoint(edge.from_id, edge.from_address),
    to: endpoint(edge.to_id, edge.to_address),
    tokenAddress: edge.token_address ?? void 0,
    kind: edge.kind ?? void 0,
    interactionCount: edge.interaction_count ?? 0,
    rawAmountTotal: edge.raw_amount_total,
    amountDisplay,
    symbol,
    valueUsd,
    fromChain,
    toChain,
    crossChain: Boolean(fromChain && toChain && fromChain !== toChain)
  };
}
function toGraphView(graph, options = {}) {
  const nodes = (graph.nodes ?? []).map((n) => nodeView(n, options));
  const edges = (graph.edges ?? []).map((e) => edgeView(e, options));
  return { nodes, edges };
}
function totalEdgeUsd(view) {
  let total = 0;
  for (const edge of view.edges) if (edge.valueUsd != null) total += edge.valueUsd;
  return total;
}
function totalEdgeUsdDisplay(view) {
  return formatUsd(totalEdgeUsd(view), { compact: true });
}
var AMOUNT_BASIS = {
  QUOTED_MINIMUM: "quoted_minimum"
};
var SemanticNodeSchema = z.lazy(
  () => z.object({
    id: z.string(),
    kind: z.string().default("unknown"),
    title: z.string(),
    protocol_id: z.union([z.string(), z.null()]).default(null),
    category: z.union([z.string(), z.null()]).default(null),
    chain_id: z.union([z.string(), z.null()]).default(null),
    actors: z.array(SemanticActorSchema).default([]),
    assets: z.array(SemanticAssetSchema).default([]),
    attributes: z.record(z.string(), z.unknown()).default({}),
    children: z.array(SemanticNodeSchema).default([]),
    source_refs: z.array(SemanticSourceRefSchema).default([]),
    confidence: z.number().min(0).max(1).default(1),
    evidence: z.union([EvidenceSchema, z.null()]).default(null),
    proxy_chain: z.array(z.string()).default([])
  })
);
z.object({
  version: z.string().default("1"),
  summary: z.union([z.string(), z.null()]).default(null),
  roots: z.array(SemanticNodeSchema).default([])
});

// src/transpose/scene.ts
function mergeTags(backend, overlay) {
  const out = [];
  const seen = /* @__PURE__ */ new Set();
  const push = (t) => {
    if (t && !seen.has(t)) {
      seen.add(t);
      out.push(t);
    }
  };
  if (Array.isArray(backend)) backend.forEach((t) => push(String(t)));
  (overlay ?? []).forEach(push);
  return out.length > 0 ? out : void 0;
}
function joinCdn(base, key) {
  return `${base.replace(/\/+$/, "")}/${key.replace(/^\/+/, "")}`;
}
function resolveMediaRef(ref, options = {}) {
  if (!ref || !ref.value) return void 0;
  const alt = ref.alt ?? void 0;
  const fallbackSlug = ref.fallback ?? void 0;
  switch (ref.kind) {
    case "url":
      return { url: ref.value, alt, slug: fallbackSlug };
    case "cdn_key": {
      if (options.cdnBase) {
        return { url: joinCdn(options.cdnBase, ref.value), slug: fallbackSlug, alt };
      }
      const url = fallbackSlug ? options.resolvePlaceholder?.(fallbackSlug) : void 0;
      return { url, slug: url ? void 0 : fallbackSlug, alt };
    }
    case "placeholder":
    default: {
      const url = options.resolvePlaceholder?.(ref.value);
      return { url, slug: url ? void 0 : ref.value, alt };
    }
  }
}
function assetLabel(asset) {
  if (!asset) return void 0;
  const amount = asset.amount ?? asset.amount_raw ?? void 0;
  const parts = [amount, asset.symbol].filter(Boolean);
  if (!parts.length) return void 0;
  const label = parts.join(" ");
  return asset.basis === AMOUNT_BASIS.QUOTED_MINIMUM ? `${label} (min)` : label;
}
function parseUsd(usd) {
  if (usd == null) return void 0;
  const n = Number.parseFloat(usd);
  return Number.isFinite(n) ? n : void 0;
}
function pointOf(value) {
  if (typeof value !== "object" || value === null) return void 0;
  const { x, y } = value;
  if (typeof x !== "number" || typeof y !== "number") return void 0;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return void 0;
  return { x, y };
}
function isFailed(attributes) {
  if (!attributes) return false;
  const status = String(attributes.status ?? "").toLowerCase();
  if (status === "failed" || status === "reverted" || status === "error" || status === "revert") {
    return true;
  }
  if (attributes.failed === true || attributes.reverted === true) return true;
  if (attributes.success === false) return true;
  return false;
}
function assetViews(assets, options) {
  return (assets ?? []).filter((a) => a.symbol || a.token_address).map((a) => ({
    tokenAddress: a.token_address ?? void 0,
    tokenChainId: a.token_chain_id ?? void 0,
    symbol: a.symbol ?? void 0,
    decimals: a.decimals ?? void 0,
    amount: a.amount ?? void 0,
    amountRaw: a.amount_raw ?? void 0,
    usd: a.usd ?? void 0,
    direction: a.direction ?? void 0,
    basis: a.basis ?? void 0,
    minAmount: a.min_amount ?? void 0,
    icon: resolveMediaRef(a.icon, options)
  }));
}
function sourceRefViews(refs) {
  return (refs ?? []).filter((r) => r.ref_type).map((r) => ({
    refType: r.ref_type,
    address: r.address ?? void 0,
    logIndex: r.log_index ?? void 0,
    internalTxIndex: r.internal_tx_index ?? void 0,
    path: r.path ?? []
  }));
}
function factViews(facts) {
  return (facts ?? []).filter((f) => f.key && f.value !== null && f.value !== void 0 && f.value !== "").map((f) => ({
    key: f.key,
    label: f.label || f.key,
    kind: f.kind ?? void 0,
    value: f.value,
    protocolId: f.protocol_id ?? void 0,
    confidence: f.confidence ?? void 0,
    description: f.description ?? void 0
  }));
}
function elementOrigin(origin, legacyVirtual) {
  if (origin && origin !== "decoded") return origin;
  return legacyVirtual ? "synthesized" : "decoded";
}
function nodeView2(node, options) {
  const address = node.address ?? void 0;
  const short = address ? shortenAddress(address) : void 0;
  const overlay = address ? options.resolveOverlay?.(address, node.chain_id ?? void 0) : void 0;
  const baseLabel = node.label && node.label !== address ? node.label : short ?? (node.label || node.id);
  const idLower = node.id.toLowerCase();
  const addressLower = address?.toLowerCase();
  const isTheAddressItself = addressLower != null && (idLower === addressLower || idLower.endsWith(`#${addressLower}`) || node.label?.toLowerCase() === addressLower);
  const label = (isTheAddressItself ? overlay?.label : void 0) ?? baseLabel;
  const identity = address && label !== short ? short : void 0;
  const sublabel = identity ? [identity, node.sublabel].filter(Boolean).join(" \xB7 ") : node.sublabel ?? void 0;
  const overlayIcon = overlay?.logo ? { url: overlay.logo, alt: overlay.label ?? label } : void 0;
  const origin = elementOrigin(node.origin, node.attributes?.virtual === true);
  const pinned = origin === "user" ? pointOf(node.attributes?.position) : void 0;
  return {
    id: node.id,
    label,
    sublabel: sublabel ?? void 0,
    chainId: node.chain_id ?? void 0,
    address,
    kind: node.kind,
    offChain: node.off_chain,
    protocol: typeof node.attributes?.protocol === "string" ? node.attributes.protocol : void 0,
    groupId: node.group_id ?? void 0,
    entityId: node.entity_id ?? overlay?.primaryEntityId,
    icon: overlayIcon ?? resolveMediaRef(node.icon, options),
    value: node.value ?? void 0,
    badge: node.badge ?? void 0,
    confidence: node.confidence,
    tags: mergeTags(node.attributes?.tags, overlay?.tags),
    // The overlay's own weight, not only its name. Without it a node labelled
    // "Fake_Phishing7575952" renders exactly like one labelled "Uniswap:
    // Router" — same chip, same colour, no badge.
    riskLevel: overlay?.riskLevel,
    riskScore: overlay?.riskScore,
    failed: isFailed(node.attributes),
    contested: claimViews(node.attributes),
    position: pinned,
    virtual: origin === "synthesized",
    origin,
    facts: factViews(node.facts),
    assets: assetViews(node.assets, options),
    sourceRefs: sourceRefViews(node.source_refs),
    detailTier: node.detail_tier ?? "compact"
  };
}
function edgeTransfers(edge) {
  const refs = mergedTxRefs(edge.attributes);
  if (refs.length === 0) return void 0;
  const decimals = edge.asset?.decimals;
  return refs.map((ref) => ({
    id: ref.id,
    label: shortenHash(ref.tx_hash ?? ref.id),
    ...ref.amount_raw != null ? { amount: decimals != null ? formatUnits(ref.amount_raw, decimals) : ref.amount_raw } : {}
  }));
}
function edgeView2(edge, options, bidirectional) {
  const origin = elementOrigin(edge.origin, edge.virtual);
  const synthesized = origin === "synthesized";
  return {
    id: edge.id,
    from: edge.source,
    to: edge.target,
    kind: edge.kind,
    // A wash is worded, not counted. The backend's label is the arithmetic
    // ("0 USDC"), which on a line between two addresses reads as "nothing
    // happened here" — the opposite of what happened.
    label: edge.attributes?.net_zero === true ? `nets to zero${edge.asset?.symbol ? ` \xB7 ${edge.asset.symbol}` : ""}` : edge.label ?? assetLabel(edge.asset),
    crossChain: edge.cross_chain,
    virtual: synthesized,
    origin,
    facts: factViews(edge.facts),
    dashed: synthesized,
    amount: edge.asset?.amount ?? edge.asset?.amount_raw ?? void 0,
    symbol: edge.asset?.symbol ?? void 0,
    valueUsd: parseUsd(edge.asset?.usd),
    basis: edge.asset?.basis ?? void 0,
    minAmount: edge.asset?.min_amount ?? edge.asset?.min_amount_raw ?? void 0,
    direction: edge.direction ?? edge.asset?.direction ?? void 0,
    bidirectional: bidirectional.has(unorderedPairKey(edge.source, edge.target)),
    netted: edge.attributes?.netted === true,
    contested: claimViews(edge.attributes),
    mergedCount: mergedCount(edge.attributes) > 1 ? mergedCount(edge.attributes) : void 0,
    transfers: edgeTransfers(edge),
    compressedVia: Array.isArray(edge.attributes?.compressed_via) ? edge.attributes.compressed_via.filter(
      (v) => typeof v === "string"
    ) : void 0,
    netZero: edge.attributes?.net_zero === true,
    failed: isFailed(edge.attributes),
    icon: resolveMediaRef(edge.icon, options),
    fromChain: edge.from_chain ?? void 0,
    toChain: edge.to_chain ?? void 0,
    detailTier: edge.detail_tier ?? "compact"
  };
}
function claimViews(attributes) {
  const raw = attributes?.[CONTESTED_ATTR];
  if (!Array.isArray(raw) || raw.length === 0) return void 0;
  const out = raw.flatMap((entry) => {
    if (typeof entry !== "object" || entry === null) return [];
    const { field, value, by, scope } = entry;
    if (typeof field !== "string") return [];
    return [
      {
        field,
        value: typeof value === "string" ? value : JSON.stringify(value ?? null),
        by: typeof by === "string" && by ? by : void 0,
        scope: typeof scope === "string" && scope ? scope : void 0
      }
    ];
  });
  return out.length > 0 ? out : void 0;
}
function unorderedPairKey(a, b) {
  return a < b ? `${a}\0${b}` : `${b}\0${a}`;
}
function bidirectionalPairs(edges) {
  const directed = /* @__PURE__ */ new Set();
  const both = /* @__PURE__ */ new Set();
  for (const edge of edges) {
    if (edge.source === edge.target) continue;
    directed.add(`${edge.source}\0${edge.target}`);
    if (directed.has(`${edge.target}\0${edge.source}`)) {
      both.add(unorderedPairKey(edge.source, edge.target));
    }
  }
  return both;
}
function groupView(group, options) {
  const colors = getChainGroupColors(group.chain_id);
  const label = (group.chain_id ? options.chainLabel?.(group.chain_id) : void 0) ?? (group.label && group.chain_id !== group.label ? group.label : colors.name);
  const tint = group.color ? brandGroupTint(group.color) : colors;
  return {
    id: group.id,
    groupType: group.group_type,
    chainId: group.chain_id ?? void 0,
    label,
    memberNodeIds: group.member_node_ids,
    fill: tint.fill,
    border: tint.border,
    text: tint.text,
    brandColor: group.color ?? colors.brandColor
  };
}
function isAddressAbbreviation(symbol, address) {
  const [head, tail, ...rest] = symbol.split("\u2026");
  if (rest.length > 0 || !head || !tail) return false;
  return symbol.length < address.length && address.startsWith(head) && address.endsWith(tail);
}
function sceneTokenSymbols(scenes) {
  const symbols = /* @__PURE__ */ new Map();
  const add = (asset) => {
    const address = asset?.token_address;
    const symbol = asset?.symbol?.trim();
    if (!address || !symbol || symbols.has(address)) return;
    if (isAddressAbbreviation(symbol, address)) return;
    symbols.set(address, symbol);
  };
  for (const scene of scenes) {
    for (const node of scene?.nodes ?? []) for (const asset of node.assets ?? []) add(asset);
    for (const edge of scene?.edges ?? []) add(edge.asset);
  }
  return symbols;
}
function toSceneView(scene, options = {}) {
  const edges = scene.edges ?? [];
  const bidirectional = bidirectionalPairs(edges);
  return {
    kind: scene.kind,
    summary: scene.summary ?? void 0,
    nodes: (scene.nodes ?? []).map((n) => nodeView2(n, options)),
    edges: edges.map((e) => edgeView2(e, options, bidirectional)),
    groups: (scene.groups ?? []).map((g) => groupView(g, options)),
    chainIds: scene.chain_ids ?? [],
    multiChain: scene.multi_chain,
    layoutHint: scene.layout_hint,
    defaultDetailTier: scene.default_detail_tier ?? "compact",
    // Only meaningful on an empty scene; the backend only stamps it there.
    emptyReason: typeof scene.attributes?.[SCENE_EMPTY_REASON_ATTR] === "string" ? scene.attributes[SCENE_EMPTY_REASON_ATTR] : void 0
  };
}

// src/transpose/detail.ts
var DETAIL_TIER_ORDER = SCENE_DETAIL_TIERS;
function detailTierRank(tier) {
  const i = DETAIL_TIER_ORDER.indexOf(tier ?? "compact");
  return i < 0 ? 0 : i;
}
function detailTierVisible(elementTier, viewTier) {
  return detailTierRank(elementTier) <= detailTierRank(viewTier);
}
function maxDetailTier(a, b) {
  return DETAIL_TIER_ORDER[Math.max(detailTierRank(a), detailTierRank(b))];
}
function containsChildren(edges) {
  const out = /* @__PURE__ */ new Map();
  for (const e of edges) {
    if (e.kind !== "contains") continue;
    const kids = out.get(e.from);
    if (kids) kids.push(e.to);
    else out.set(e.from, [e.to]);
  }
  return out;
}
function isAlwaysVisible(node) {
  return node.origin === "user";
}
function visibleNodeIds(view, tier, expanded) {
  const visible = /* @__PURE__ */ new Set();
  for (const n of view.nodes) {
    if (isAlwaysVisible(n) || detailTierVisible(n.detailTier, tier)) visible.add(n.id);
  }
  if (expanded.size === 0) return visible;
  const children = containsChildren(view.edges);
  const seen = /* @__PURE__ */ new Set();
  const stack = [];
  for (const id of expanded) if (visible.has(id)) stack.push(id);
  while (stack.length) {
    const id = stack.pop();
    for (const child of children.get(id) ?? []) {
      if (!visible.has(child)) visible.add(child);
      if (expanded.has(child) && !seen.has(child)) {
        seen.add(child);
        stack.push(child);
      }
    }
  }
  return visible;
}
function filterSceneViewByDetailTier(view, tier, options = {}) {
  const visible = visibleNodeIds(view, tier, new Set(options.expandedNodeIds ?? []));
  const nodes = view.nodes.filter((n) => visible.has(n.id));
  const edges = view.edges.filter((e) => visible.has(e.from) && visible.has(e.to));
  const groups = view.groups.map((g) => ({ ...g, memberNodeIds: g.memberNodeIds.filter((id) => visible.has(id)) })).filter((g) => g.memberNodeIds.length > 0);
  return { ...view, nodes, edges, groups };
}
function nodesWithHiddenChildren(view, tier, options = {}) {
  const children = containsChildren(view.edges);
  if (children.size === 0) return /* @__PURE__ */ new Set();
  const expanded = new Set(options.expandedNodeIds ?? []);
  const visible = visibleNodeIds(view, tier, expanded);
  const out = /* @__PURE__ */ new Set();
  for (const [parent, kids] of children) {
    if (!visible.has(parent) || expanded.has(parent)) continue;
    for (const child of kids) {
      if (!visible.has(child)) {
        out.add(parent);
        break;
      }
    }
  }
  return out;
}

// src/transpose/assignment.ts
function hasAssignmentActions(handlers) {
  if (!handlers) return false;
  return Boolean(
    handlers.onSetLabel || handlers.onAddTag || handlers.onRemoveTag || handlers.onAddGroup || handlers.onRemoveGroup || handlers.onAssignEntity || handlers.onClearEntity
  );
}
function mergeAssignmentState(prev, change) {
  const next = { ...prev };
  if (change.label !== void 0) next.label = change.label;
  if (change.entityId !== void 0) next.entityId = change.entityId;
  if (change.entityLabel !== void 0) next.entityLabel = change.entityLabel;
  if (change.tags !== void 0) next.tags = [...change.tags];
  if (change.groups !== void 0) next.groups = [...change.groups];
  if (change.addTag) next.tags = withMember(next.tags, change.addTag);
  if (change.removeTag) next.tags = withoutMember(next.tags, change.removeTag);
  if (change.addGroup) next.groups = withMember(next.groups, change.addGroup);
  if (change.removeGroup) next.groups = withoutMember(next.groups, change.removeGroup);
  return next;
}
function withMember(current, member) {
  const members = new Set(current ?? []);
  members.add(member);
  return [...members];
}
function withoutMember(current, member) {
  return (current ?? []).filter((m) => m !== member);
}

export { DETAIL_TIER_ORDER, TOKEN_FLOW_KINDS, coerceTransactionRow, coerceTransactionRows, detailTierRank, detailTierVisible, filterSceneViewByDetailTier, graphEdgeId, hasAssignmentActions, isAddressAbbreviation, maxDetailTier, mergeAssignmentState, nodesWithHiddenChildren, resolveMediaRef, sceneTokenSymbols, toGraphView, toNetFlowRows, toSceneView, toTokenFlowRows, toUniqueAddresses, totalEdgeUsd, totalEdgeUsdDisplay, txEntryUniqueAddresses, unwrapTxEnvelope };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map